/**
 * Carrying out the post-debrief decision, behind the board's undo countdown.
 *
 * Same shape as `offerEmailAction` and `moveEmailAction`: the board hands these
 * to the queue and nothing happens until the countdown expires. That matters
 * most on the rejection — it archives a candidate in Lever AND emails them, so
 * "Stop" has to mean neither happened.
 *
 * Both call the SAME `/action` route the Feedback tab's Move and Archive use.
 * There is deliberately no second archive path: two of them means one of them
 * silently sends nothing, which is exactly the bug the Archive modal already
 * had once.
 */
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import type { SchedulingCandidate } from '../../types/taActionBoard';
import type { PendingAction } from './usePendingActions';

/** Move the candidate to the offer stage. No email — the offer lives in Lever. */
export function debriefOfferAction({
  candidate,
  boardEmail,
  targetStageId,
}: {
  candidate: SchedulingCandidate;
  boardEmail: string;
  targetStageId: string;
}): Omit<PendingAction, 'secondsLeft'> {
  return {
    id: `debrief-offer-${candidate.opportunityId}`,
    label: `Moving ${candidate.name} to offer`,
    run: async () => {
      try {
        const r = await api.postAction({
          type: 'move',
          opportunityId: candidate.opportunityId,
          boardEmail,
          targetStageId,
        });
        showToast({
          status: 'success',
          description: `${candidate.name} moved to ${r.movedTo || 'the offer stage'} in Lever.`,
        });
      } catch (e) {
        // Named as NOT done: the board has already muted the card, so a silent
        // failure would leave a candidate parked in the final-round stage with
        // nothing on screen saying so.
        showToast({
          status: 'error',
          description: `${candidate.name} was NOT moved: ${
            e instanceof Error && e.message ? e.message : 'unknown error'
          }. Move them in Lever by hand.`,
        });
      }
    },
    undo: () =>
      showToast({ status: 'info', description: `Stopped — ${candidate.name} was not moved.` }),
  };
}

/** Archive with the Lever reason, and send the rejection when one was approved. */
export function debriefRejectAction({
  candidate,
  boardEmail,
  reason,
  email,
  gmailToken,
}: {
  candidate: SchedulingCandidate;
  boardEmail: string;
  reason: string;
  /** null = archive without sending anything, which is a decision a recruiter may make. */
  email: { subject: string; bodyHtml: string; language: 'de' | 'en' } | null;
  gmailToken: string | null;
}): Omit<PendingAction, 'secondsLeft'> {
  return {
    id: `debrief-reject-${candidate.opportunityId}`,
    label: email
      ? `Rejecting ${candidate.name} and sending the email`
      : `Archiving ${candidate.name}`,
    run: async () => {
      try {
        const r = await api.postAction(
          {
            type: 'archive',
            opportunityId: candidate.opportunityId,
            boardEmail,
            reason,
            ...(email ? { email } : {}),
          },
          gmailToken
        );
        /**
         * Reported part by part, like the case booking: an archive whose email
         * failed is a REAL state the recruiter has to finish by hand, and a flat
         * "rejected" would hide exactly the half that needs them.
         */
        const emailNote = !email
          ? ' No email was sent.'
          : r.emailSent
            ? ` Rejection sent to ${r.to}.`
            : ` ⚠ The rejection was NOT sent (${r.reason || 'unknown reason'}) — send it yourself.`;
        showToast({
          status: !email || r.emailSent ? 'success' : 'warning',
          description: `${candidate.name} archived in Lever as “${reason}”.${emailNote}`,
        });
      } catch (e) {
        showToast({
          status: 'error',
          description: `${candidate.name} was NOT archived: ${
            e instanceof Error && e.message ? e.message : 'unknown error'
          }. Nothing was sent.`,
        });
      }
    },
    undo: () =>
      showToast({
        status: 'info',
        description: `Stopped — ${candidate.name} was not archived and nothing was sent.`,
      }),
  };
}
